// src/services/gapfamilyAPI.js
//
// Production-job data from gapfamily-service (NestJS over MySQL gapintegrity).
// Provides nomor job + WIP position for a product, to complement Accurate
// (which has no production-status data). See memory/gapfamily-service.md.
//
// NOTE: the reachable dev DB (10.3.0.85) has an empty `datajob`; real jobs live
// on the prod DB (VITE_GAPFAMILY_URL → prod backend). The code path is ready.

import { gapfamilyApi } from './api';

// WIP position → human label + colour. finish/closed/null = produksi selesai.
export const wipStatus = (pos) => {
  const p = (pos || '').toLowerCase();
  if (!p || p === 'finish' || p === 'selesai') return { label: 'Selesai', color: '#16a34a', bg: '#dcfce7' };
  if (p.includes('closed') || p === 'c_closed') return { label: 'Closed', color: '#16a34a', bg: '#dcfce7' };
  if (p === 'start') return { label: 'Belum Mulai', color: '#64748b', bg: '#f1f5f9' };
  return { label: pos, color: '#d97706', bg: '#fef3c7' }; // di workcenter tertentu = WIP
};

// Batch: kirim banyak kode produk → map { kode: jobInfo }. Untuk tabel produksi.
// Catatan: endpoint POST {gapfamily}/jobs-progress/lookup-batch (port 3005).
// - Kode dinormalkan ke STRING (sursys kadang mengirim kode sebagai number).
// - Permintaan dipecah per-chunk supaya aman bila daftar kode panjang.
// - Bila gagal, map kosong dikembalikan TAPI error dilempar lewat console agar
//   penyebab kelihatan (status produksi yang "—" biasanya gejala batch gagal).
export const lookupJobsBatch = async (codes, chunkSize = 200) => {
  const list = [...new Set((codes || []).map((c) => (c == null ? '' : String(c).trim())).filter(Boolean))];
  if (!list.length) return {};

  const merged = {};
  for (let i = 0; i < list.length; i += chunkSize) {
    const slice = list.slice(i, i + chunkSize);
    try {
      const { data } = await gapfamilyApi.post('/jobs-progress/lookup-batch', { codes: slice });
      const map = (data?.data ?? data) || {};
      Object.assign(merged, map);
    } catch (e) {
      console.error(
        `lookupJobsBatch gagal (gapfamily ${gapfamilyApi.defaults.baseURL}/jobs-progress/lookup-batch):`,
        e?.response?.status,
        e?.response?.data || e?.message,
      );
    }
  }
  return merged;
};

// Riwayat produksi lengkap 1 produk (per tahun) untuk drawer klik No Job.
export const fetchJobHistory = async (code) => {
  if (!code) return null;
  try {
    const { data } = await gapfamilyApi.get('/jobs-progress/history', { params: { code } });
    return data?.data ?? data ?? null;
  } catch (e) {
    console.error('fetchJobHistory failed', e);
    return null;
  }
};

// Lookup production jobs by product code or name. Returns { found, total, jobs:[...] }.
// gapfamily wraps responses via a global interceptor as { status, data: {...} } — unwrap it.
export const lookupJob = async (keyword) => {
  if (!keyword || !keyword.trim()) return { found: false, total: 0, jobs: [] };
  try {
    const { data } = await gapfamilyApi.get('/jobs-progress/lookup', { params: { keyword } });
    const payload = data?.data ?? data;
    return payload || { found: false, total: 0, jobs: [] };
  } catch (e) {
    console.error('lookupJob failed', e);
    return { found: false, total: 0, jobs: [], error: e?.message };
  }
};


// ── Daftar JOB PRODUKSI DIGITAL dari tabel `datajob` ─────────────────────────
//
// Dipakai untuk MERGE dengan data struk (POS sursys):
//   - ada di datajob, tidak ada di POS  -> job tanpa struk (belum ditagih)
//   - ada di POS, tidak ada di datajob  -> struk tanpa job produksi
//
// KONTRAK ENDPOINT:
//   GET {gapfamily}/digital/analytics-dashboard?startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
//   -> { status, data: [ { no_job, wilayah, tanggal, customer, judul, ... } ] }
//
// Endpoint bisa dioverride lewat env VITE_GAPFAMILY_DIGITAL_PATH bila berbeda.
// Normalisasi di bawah toleran terhadap beberapa kemungkinan nama field, jadi
// kalau nama kolom sedikit beda (mis. nomor_job / wilayahDigital / tgl), tetap
// terbaca tanpa ubah kode.
const DIGITAL_JOBS_PATH =
  import.meta.env.VITE_GAPFAMILY_DIGITAL_PATH || '/digital/analytics-dashboard';

// Master tabel `wilayah_digital` (kecil & statis) — id -> nama_wilayah.
// Dipakai untuk me-resolve datajob.wilayahDigital_id menjadi nama wilayah di
// sisi frontend, sehingga endpoint cukup mengembalikan wilayahDigital_id
// (kalau backend sudah JOIN dan mengirim nama_wilayah, itu tetap diutamakan).
export const WILAYAH_DIGITAL = {
  1: 'Ambon',
  2: 'Jakarta',
  3: 'Surabaya',
  4: 'Purwakarta',
  5: 'Bali',
  6: 'Produk Development',
  7: 'Yogyakarta',
  8: 'Bandung',
};

export const resolveWilayah = (id) => {
  if (id == null || id === '') return null;
  return WILAYAH_DIGITAL[Number(id)] || null;
};

const pick = (obj, keys, fallback = undefined) => {
  for (const k of keys) {
    if (obj && obj[k] != null && obj[k] !== '') return obj[k];
  }
  return fallback;
};

// Normalisasi satu baris datajob ke bentuk seragam untuk UI/merge.
export const normalizeDigitalJob = (raw = {}) => {
  const noJobRaw = pick(raw, ['no_job', 'nomor_job', 'jobNo', 'job_no', 'nojob', 'no']);
  const noJob = noJobRaw != null ? String(noJobRaw).trim().toUpperCase() : null;

  // wilayah: utamakan nama yang sudah di-join backend, jika tidak ada resolve
  // dari wilayahDigital_id lewat master statis di atas.
  const wilayahId = pick(raw, ['wilayahDigital_id', 'wilayah_id', 'wilayahDigitalId', 'id_wilayah']);
  const wilayahName =
    pick(raw, ['nama_wilayah', 'wilayah', 'wilayahDigital', 'nama_wilayah_digital']) ||
    resolveWilayah(wilayahId) ||
    'Tanpa Wilayah';

  const isDigital = pick(raw, ['isDigitalJob', 'isDigital', 'is_digital'], null);

  return {
    noJob,
    isDigital: isDigital == null ? null : Number(isDigital) === 1 || isDigital === true,
    wilayahId: wilayahId != null ? Number(wilayahId) : null,
    wilayah: wilayahName,
    tanggal: pick(raw, ['tanggal', 'tgl', 'date', 'created_at', 'createdAt'], ''),
    customer: pick(raw, ['customer', 'customername', 'nama_pemesan', 'pemesan', 'instansi'], '-'),
    judul: pick(raw, ['judul', 'judul_job', 'nama_buku', 'keterangan', 'deskripsi'], ''),
    kategori: pick(raw, ['kategori', 'category', 'categorycode'], '-'),
    oplah: Number(pick(raw, ['oplah', 'qty', 'quantity'], 0)) || 0,
    raw,
  };
};

/**
 * Ambil daftar job digital dari datajob untuk satu rentang tanggal.
 * Hanya job digital (isDigitalJob = 1) yang diambil; jika field flag tidak
 * dikirim backend, semua baris dianggap sudah terfilter digital.
 * @returns {Promise<{ available:boolean, jobs:Array, error?:string }>}
 */
export const fetchDigitalJobs = async (startDate, endDate) => {
  try {
    const { data } = await gapfamilyApi.get(DIGITAL_JOBS_PATH, {
      params: { startDate, endDate },
    });

    // Backend membungkus respons: { status:'success', data:{ status:true, data:[...] } }.
    // Buka selapis demi selapis sampai menemukan array baris job.
    let payload = data?.data ?? data;
    if (payload && !Array.isArray(payload) && payload.data) payload = payload.data;
    const arr = Array.isArray(payload) ? payload : payload?.jobs || [];

    const jobs = arr
      .map(normalizeDigitalJob)
      .filter(
        (j) =>
          j.noJob &&
          j.isDigital !== false &&
          // Kecualikan Product Development (prefix "PD") — bukan job toko digital.
          !j.noJob.startsWith('PD') &&
          // SEMENTARA: tampilkan hanya wilayah Jakarta (wilayahDigital_id = 2).
          j.wilayahId === 2
      );

    return { available: true, jobs };
  } catch (e) {
    console.warn('fetchDigitalJobs (datajob) tidak tersedia:', e?.message);
    return { available: false, jobs: [], error: e?.message };
  }
};
